import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutStats, aboutFeatures } from '@/mocks/comics';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9p1e4aqs38sj6jc7hf0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;

    try {
      const formData = new FormData(form);
      const honeypot = formData.get('phone_alt');
      if (honeypot && String(honeypot).trim() !== '') {
        setFormStatus('success');
        setSubmitting(false);
        form.reset();
        return;
      }
      formData.delete('phone_alt');

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (
        response.ok &&
        parsed &&
        (parsed as Record<string, unknown>).code === 'OK'
      ) {
        setFormStatus('success');
        form.reset();
      } else {
        const msg =
          (parsed as Record<string, unknown>)?.meta &&
          typeof ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message === 'string'
            ? String(((parsed as Record<string, unknown>).meta as Record<string, unknown>).message)
            : '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }

    setSubmitting(false);
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩国漫画网_韩漫在线阅读"
        description="韩国漫画网品牌介绍，了解我们的使命与价值观。我们提供无删减下拉式韩漫大全免费在线阅读，服务数百万漫画爱好者。"
        keywords="韩国漫画网,关于我们,韩漫在线阅读,免费韩国漫画"
        canonicalUrl="https://koreanmanga.example.com/about"
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于韩国漫画网',
            description: '韩国漫画网品牌介绍与联系方式',
            url: 'https://koreanmanga.example.com/about',
          },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '关于我们' },
            ]}
          />

          <section className="py-8 md:py-12" aria-labelledby="about-heading">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h1
                id="about-heading"
                className="font-heading font-bold text-3xl md:text-4xl text-foreground-900"
              >
                关于韩国漫画网
              </h1>
              <p className="mt-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                韩国漫画网成立于2020年，是国内领先的<strong>韩漫在线阅读平台</strong>。我们致力于为中文读者提供
                <strong>免费韩国漫画</strong>，以<strong>无删减</strong>、<strong>下拉式大全</strong>的形式呈现
                <strong>完整内容</strong>，确保作品内容的完整性与原汁原味，为您保留最真实的故事情节。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
              {[
                { value: aboutStats.totalManga, label: '韩漫作品', icon: 'ri-book-open-line', suffix: '+' },
                { value: aboutStats.totalChapters, label: '漫画章节', icon: 'ri-file-list-2-line', suffix: '+' },
                { value: aboutStats.totalViews, label: '累计阅读', icon: 'ri-eye-line', suffix: '亿+' },
                { value: aboutStats.dailyActiveUsers, label: '日活跃用户', icon: 'ri-user-line', suffix: '+' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100"
                >
                  <div className="w-10 h-10 mx-auto flex items-center justify-center text-primary-500 mb-3">
                    <i className={`${stat.icon} text-2xl`}></i>
                  </div>
                  <div className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
                    {stat.value >= 100000000
                      ? `${(stat.value / 100000000).toFixed(1)}`
                      : stat.value >= 10000
                      ? `${(stat.value / 10000).toFixed(0)}`
                      : stat.value}
                    {stat.suffix}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-20">
              <h2 className="font-heading font-bold text-2xl text-foreground-900 text-center mb-10">
                我们的优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {aboutFeatures.map((feature) => (
                  <div
                    key={feature.title}
                    className="p-6 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors"
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center text-primary-500 mb-4">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="font-heading font-bold text-2xl text-foreground-900 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '漫画爱好者小王',
                    text: '一直在韩国漫画网看韩漫，更新速度快，画质清晰，下拉式阅读体验太好了！强烈推荐给喜欢韩漫的朋友。',
                    rating: 5,
                  },
                  {
                    name: '资深漫迷小李',
                    text: '这是我用过最好的韩漫平台，无删减的内容让我能看到原汁原味的作品。分类也很清晰，找漫画很方便。',
                    rating: 5,
                  },
                  {
                    name: '日常读者小张',
                    text: '每天通勤路上都会打开韩国漫画网看几话，免费的韩漫资源太良心了。希望网站越来越好！',
                    rating: 5,
                  },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-xl bg-background-100"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-400">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{review.text}&rdquo;
                    </p>
                    <p className="text-sm font-medium text-foreground-900">
                      — {review.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="max-w-2xl mx-auto" id="contact-section">
              <h2 className="font-heading font-bold text-2xl text-foreground-900 text-center mb-4">
                联系我们
              </h2>
              <p className="text-center text-sm text-foreground-500 mb-8">
                有任何问题或建议？请填写下方表单，我们会尽快回复您。
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-100 rounded-2xl p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入消息主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <div className="frm-hnypt">
                  <label htmlFor="phone_alt">备用联系方式</label>
                  <input
                    type="text"
                    id="phone_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitting ? '提交中...' : '发送消息'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 bg-green-50 px-4 py-2.5 rounded-lg">
                    感谢您的留言！我们会尽快与您联系。
                  </p>
                )}

                {formStatus === 'error' && (
                  <p className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                    {formError || '提交失败，请稍后再试。'}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}